import { Link as RouterLink } from 'react-router-dom';
import { Avatar, Box, Card, CardContent, ListItemButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAddAltOutlined';
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined';
import EventIcon from '@mui/icons-material/EventAvailableOutlined';
import type { ActivityEvent } from '../../lib/dashboard-health';
import { formatRupiah } from '../../lib/format';
import { useLanguage } from '../../i18n';

/** "Aktivitas terbaru": what just happened, newest first. */
export function ActivityFeed({ events, now }: { events: ActivityEvent[]; now: Date }) {
  const { lang, t } = useLanguage();
  const theme = useTheme();

  const ago = (iso: string) => {
    const minutes = Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 60_000));
    if (minutes < 60) return t('dash.ago.minutes', { count: Math.max(1, minutes) });
    const hours = Math.round(minutes / 60);
    if (hours < 24) return t('dash.ago.hours', { count: hours });
    const days = Math.round(hours / 24);
    if (days === 1) return t('dash.ago.yesterday');
    return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'id-ID', { day: 'numeric', month: 'short' });
  };

  const look = (e: ActivityEvent) => {
    switch (e.kind) {
      case 'PAYMENT':
        return { Icon: PaymentsIcon, color: theme.palette.success.main, text: t('dash.feed.payment', { unit: e.unit, amount: formatRupiah(e.amountCents) }), to: '/invoices?status=PAID' };
      case 'SIGNUP':
        return { Icon: PersonAddIcon, color: theme.palette.secondary.main, text: e.unit ? t('dash.feed.signup', { name: e.name, unit: e.unit }) : t('dash.feed.signupPending', { name: e.name }), to: '/residents' };
      case 'COMPLAINT':
        return { Icon: FeedbackIcon, color: theme.palette.warning.main, text: t('dash.feed.complaint', { title: e.title }), to: `/complaints/${e.id}` };
      case 'BOOKING':
        return { Icon: EventIcon, color: theme.palette.info.main, text: t('dash.feed.booking', { facility: e.facility }), to: '/bookings' };
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>{t('dash.feed.title')}</Typography>
      </CardContent>
      {events.length === 0 ? (
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">{t('dash.feed.empty')}</Typography>
        </CardContent>
      ) : (
        <Box sx={{ pb: 1 }}>
          {events.map((e, i) => {
            const { Icon, color, text, to } = look(e);
            return (
              <ListItemButton key={`${e.kind}-${e.at}-${i}`} component={RouterLink} to={to} sx={{ px: 2, py: 1, gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(color, 0.14), color }}>
                  <Icon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>{text}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {ago(e.at)}
                </Typography>
              </ListItemButton>
            );
          })}
        </Box>
      )}
    </Card>
  );
}
